
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

export default function StartFreeTrialPage() {
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll to toggle navigation background/shadow
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Dynamically load the external form script and clean up on unmount
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://link.msgsndr.com/js/form_embed.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      // Guard against the script already being removed
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/e31f2f4702878072217cd119276d8644/5b56961b60c36d4ff2ff86c82d13a1e4.png"
              alt="AI Receptionist Logo"
              className="h-12 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Home
            </Link>
            <Link
              to="/demo"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Demo
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              About
            </Link>
            <Link
              to="/contact"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Contact
            </Link>
            <Link
              to="/start-free-trial"
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full mb-6">
            <i className="ri-calendar-check-fill text-purple-600 text-lg"></i>
            <span className="text-sm font-medium text-purple-700">Book Your Free Demo</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-5 leading-tight">
            Start Your 7-Day<br />Free Trial Today
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Pick a time that works for you and we&apos;ll walk you through everything. See how AI
            Receptionist can transform your business communications in just 15 minutes.
          </p>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="pb-12 px-6">
        <div className="max-w-3xl mx-auto flex flex-wrap justify-center gap-8">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <i className="ri-check-line text-green-600 text-sm"></i>
            </div>
            <span className="text-sm text-gray-600 font-medium">No credit card required</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <i className="ri-check-line text-green-600 text-sm"></i>
            </div>
            <span className="text-sm text-gray-600 font-medium">Cancel anytime</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <i className="ri-check-line text-green-600 text-sm"></i>
            </div>
            <span className="text-sm text-gray-600 font-medium">Setup in under 5 minutes</span>
          </div>
        </div>
      </section>

      {/* Calendar Booking Section */}
      <section className="pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            {/* Left Info Panel */}
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">What to Expect</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-vidicon-line text-purple-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">15-Min Live Demo</p>
                      <p className="text-sm text-gray-500">
                        See the AI receptionist handle real calls tailored to your business.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-settings-3-line text-purple-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Custom Setup</p>
                      <p className="text-sm text-gray-500">
                        We&apos;ll configure the AI to match your business needs on the spot.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-question-answer-line text-purple-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Q&amp;A Session</p>
                      <p className="text-sm text-gray-500">
                        Ask anything — pricing, integrations, features, or custom plans.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Call CTA Card */}
              <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-8 text-white">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-phone-fill text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Prefer to Call?</h3>
                <p className="text-white/80 text-sm mb-4">
                  Try our AI receptionist live right now. It answers 24/7!
                </p>
                <a
                  href="tel:+18454424515"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-purple-700 font-semibold rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-phone-line"></i>
                  Call (845) 442-4515
                </a>
              </div>

              {/* Social Proof */}
              <div className="bg-gray-50 rounded-2xl p-8">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-sm text-gray-600 italic leading-relaxed mb-4">
                  &ldquo;We booked our demo on a Friday and were fully set up by Monday. The AI
                  handles 80% of our calls now — it&apos;s been a game changer.&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden">
                    <img
                      src="https://public.readdy.ai/ai/img_res/d6f0dd38e4bc74993a62ade37808ee91.jpg"
                      alt="Sarah M."
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Sarah M.</p>
                    <p className="text-xs text-gray-500">Owner, Bright Dental Studio</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Calendar Embed */}
            <div className="lg:col-span-3">
              <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Pick a Time</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Select a date and time that works best for your schedule.
                </p>
                <iframe
                  src="https://api.leadconnectorhq.com/widget/booking/DQ1t7vf4r5J2KvUyyI9u"
                  style={{ width: '100%', border: 'none', overflow: 'hidden', minHeight: '700px' }}
                  scrolling="no"
                  id="DQ1t7vf4r5J2KvUyyI9u_booking_iframe"
                  title="Book a Call"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
          <p className="text-gray-600 mb-8 max-w-xl mx-auto">
            Our team is happy to help. Reach out anytime and we&apos;ll get back to you within 24
            hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-full hover:border-purple-600 hover:text-purple-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line text-lg"></i>
              Contact Us
            </Link>
            <Link
              to="/demo"
              className="px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-full hover:border-purple-600 hover:text-purple-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              Watch Demo First
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
